import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "Proven fundraising strategies for athletes: how to set realistic targets, write updates that get shared, and build momentum toward a season or competition goal.";

const FAQS = [
  {
    q: 'What makes a fundraising target effective?',
    a: 'A target that\'s specific and tied to a date. "I need €4,800 by March 1 to cover my entries and travel for the European junior circuit" gives supporters something concrete to contribute toward, unlike a vague ask like "money for athletics."',
  },
  {
    q: 'Is recurring support better than one-off contributions?',
    a: "Recurring support tends to add up to more over a season and is more predictable to budget against. Ten supporters giving $20/month adds up to $2,400 a year — often the difference between racing a full season and a half season.",
  },
  {
    q: 'How often should I post updates while fundraising?',
    a: "Pick a cadence — weekly, fortnightly, or after each competition — and stick to it. The biggest mistake athletes make is posting a flurry of updates in week one and then going silent for months.",
  },
  {
    q: 'Should I offer different contribution amounts to supporters?',
    a: "Yes — tiering your ask helps. For example: $5–$25 one-off for a competition entry, $10–$50/month recurring for ongoing costs like coaching, $100+ one-off for a travel block, and $100–$500/month for sponsorship-style monthly support.",
  },
  {
    q: 'Do I need to thank every single contribution?',
    a: "Yes, every one. A short, specific thank-you — naming what the contribution covered — goes further than a generic auto-reply, and supporters who feel seen are more likely to contribute again.",
  },
];

const SmartGivingGuide: React.FC = () => {
  usePageMeta('Smart Fundraising', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/team-huddle.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Smart Fundraising' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "set-a-target-that-s-specific-and-tied-to-a-date",
      text: "Set a target that's specific and tied to a date"
    },
    {
      id: "recurring-beats-one-off",
      text: "Recurring beats one-off"
    },
    {
      id: "post-updates-on-a-rhythm-not-a-feeling",
      text: "Post updates on a rhythm, not a feeling"
    },
    {
      id: "tell-them-what-their-money-did",
      text: "Tell them what their money did"
    },
    {
      id: "tier-your-ask",
      text: "Tier your ask"
    },
    {
      id: "don-t-be-precious-about-asking",
      text: "Don't be precious about asking"
    },
    {
      id: "thank-every-time",
      text: "Thank, every time"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">Smart Fundraising: Strategies That Actually Work for Athletes</h1>
      <p className="text-gray-500 mb-8 text-lg">The lessons we've learned from athletes who consistently meet their season's funding targets.</p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Some athletes set up a fund page and watch contributions trickle in. Others set up the same page and consistently fund their season — equipment, travel, coaching, entries — without stress. The difference isn't talent or popularity. It's a few habits that compound over the course of a season.
        </p>

        <h2 id="set-a-target-that-s-specific-and-tied-to-a-date">Set a target that's specific and tied to a date</h2>
        <p>
          "I need money for athletics" is not a target. "I need €4,800 by March 1 to cover my entries and travel for the European junior circuit" is. Specific, dated targets let supporters see what they're contributing toward — and let them feel the difference when they help close the gap.
        </p>
        <p>
          Break your annual cost into monthly or quarterly chunks. A €12,000 season is intimidating. €1,000 a month is something a family member can commit to with a recurring contribution.
        </p>

        <h2 id="recurring-beats-one-off">Recurring beats one-off</h2>
        <p>
          A single $50 contribution is great. A $10/month recurring contribution for twelve months is $120 — and, more importantly, it's predictable income you can budget against. Make it easy for supporters to choose monthly: most will if you ask directly.
        </p>
        <p>
          The maths matters more than you'd think. Ten supporters contributing $20/month gives you $2,400 a year, which can be the difference between racing a full season and racing half a season.
        </p>

        <h2 id="post-updates-on-a-rhythm-not-a-feeling">Post updates on a rhythm, not a feeling</h2>
        <p>
          The biggest mistake we see: athletes post a flurry of updates the first week, then go silent for three months. Pick a cadence — weekly, fortnightly, or after each competition — and stick to it.
        </p>
        <p>
          Updates don't have to be polished. A 30-second phone video from training. A photo of your kit laid out before a race. A paragraph after a competition: what worked, what didn't, what's next. Supporters care about being inside the journey, not about production quality.
        </p>

        <h2 id="tell-them-what-their-money-did">Tell them what their money did</h2>
        <p>
          When supporters can connect a specific outcome to their contribution, they give again. "Thanks to last month's contributions I covered the entry fee for X, finished Y, and Z is the next target." This is the single most under-used move on fundraising pages, and the most effective.
        </p>

        <h2 id="tier-your-ask">Tier your ask</h2>
        <p>Different supporters can give different amounts. Give them clear options:</p>
        <ul>
          <li><strong>$5–$25 one-off</strong>: covers a competition entry or a training session.</li>
          <li><strong>$10–$50/month recurring</strong>: covers ongoing essentials like coaching or gear.</li>
          <li><strong>$100+ one-off</strong>: covers a travel block or a major equipment purchase.</li>
          <li><strong>Sponsorship-style monthly</strong>: a parent, employer, or local business committing $100–$500/month for a season.</li>
        </ul>

        <h2 id="don-t-be-precious-about-asking">Don't be precious about asking</h2>
        <p>
          The single biggest barrier to athletes getting funded is asking too softly. Your family, friends, club and sponsors want to help — they just need a clear, specific request and an easy way to act on it. Sending the link with a one-line explanation is not "begging." It's giving people who already care about you a way to be part of what you're doing.
        </p>

        <h2 id="thank-every-time">Thank, every time</h2>
        <p>
          Every contribution gets a personal thank-you. Yes, every one. The Sport Fund dashboard makes it easy — and supporters who feel seen contribute again. A two-line "Thanks Aunt Jane, that covered my entry to Saturday's regional — I'll send you the result on Sunday" goes further than a generic auto-reply.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/engaging-your-supporters" className="text-blue-600">Engaging Your Supporters</Link> — how to write updates that keep family, friends and club connected through a long season.
        </p>
        <ArticleFeedback articleSlug="smart-fundraising-guide" />
      </article>
    </div>
  );
};

export default SmartGivingGuide;
